import {
  buildClientPropertyUpdatePayload,
  clientPropertyFormFromProfile,
  hasClientMaintainedProperty,
  normalizeClientPropertyInput,
} from "./clientReMyProperty";
import { formatMyPropertyAddressLine } from "./nominatimGeocode";

function safeStr(value) {
  return String(value ?? "").trim();
}

/** Virtual id when portfolio uses legacy users/{uid} myProperty* fields only. */
export const LEGACY_CLIENT_PROPERTY_ID = "__legacy_primary__";

export const CLIENT_PROPERTY_STATUSES = ["owned", "considering", "selling", "sold", "archived"];

const STATUS_SET = new Set(CLIENT_PROPERTY_STATUSES);

const STATUS_LABELS = {
  owned: "Owned",
  considering: "Considering",
  selling: "Selling",
  sold: "Sold",
  archived: "Archived",
};

export function normalizeClientPropertyStatus(value) {
  const key = safeStr(value).toLowerCase();
  return STATUS_SET.has(key) ? key : "owned";
}

export function getClientPropertyStatusLabel(status) {
  return STATUS_LABELS[normalizeClientPropertyStatus(status)] || "Owned";
}

export function isLegacyClientPropertyId(propertyId) {
  return safeStr(propertyId) === LEGACY_CLIENT_PROPERTY_ID;
}

export function isArchivedClientProperty(record) {
  return normalizeClientPropertyStatus(record?.status) === "archived";
}

/**
 * True when the user doc has any client-maintained myProperty* data worth showing as legacy primary.
 */
export function hasLegacyClientPropertyOnUser(userProfile) {
  return hasClientMaintainedProperty(userProfile);
}

/**
 * Map a Firestore myProperties record to myProperty* profile fields for existing RE components.
 */
export function propertyRecordToMyPropertyPatch(record) {
  const r = record && typeof record === "object" ? record : {};
  const street = safeStr(r.streetLine) || safeStr(r.streetAddress);
  const postal = safeStr(r.postalCode) || safeStr(r.postal);
  const full =
    safeStr(r.fullAddress) ||
    formatMyPropertyAddressLine({
      unit: r.unit,
      street,
      city: r.city,
      postal,
    }) ||
    null;

  return {
    myPropertyAddressLine: full,
    myPropertyFullAddress: full,
    myPropertyUnit: safeStr(r.unit) || null,
    myPropertyStreetLine: street || null,
    myPropertyStreetAddress: street || null,
    myPropertyCity: safeStr(r.city) || null,
    myPropertyProvince: safeStr(r.province) || null,
    myPropertyPostalCode: postal || null,
    myPropertyPostal: postal || null,
    myPropertyLat: r.lat != null ? r.lat : null,
    myPropertyLng: r.lng != null ? r.lng : null,
    myPropertyGeocode: r.geocode || null,
    myPropertyParcelSnapshot: r.parcelSnapshot || null,
    myPropertyParcelRoll: safeStr(r.parcelRoll) || null,
    myPropertyParcelYear: r.parcelYear != null ? String(r.parcelYear) : null,
    myPropertyParcelCommunity: safeStr(r.parcelCommunity) || null,
    myPropertyParcelAssessedValue:
      r.parcelAssessedValue != null && r.parcelAssessedValue !== "" ? r.parcelAssessedValue : null,
    myPropertyType: safeStr(r.propertyType) || null,
    myPropertyNeighbourhood: safeStr(r.neighbourhood) || null,
    myPropertyHomeownerStatus: safeStr(r.homeownerStatus) || null,
    myPropertyMortgageRenewalDate: safeStr(r.mortgageRenewalDate) || null,
    myPropertyInsuranceRenewalDate: safeStr(r.insuranceRenewalDate) || null,
    myPropertyLawyerName: safeStr(r.lawyerName) || null,
    myPropertyMortgageBrokerName: safeStr(r.mortgageBrokerName) || null,
    myPropertyInsuranceContactName: safeStr(r.insuranceContactName) || null,
    myPropertyNotes: safeStr(r.notes) || null,
    myPropertyMarketSnapshotInputs: r.marketSnapshotInputs || null,
    myPropertyCmaIntelligenceInputs: r.cmaIntelligenceInputs || null,
  };
}

/**
 * Virtual legacy property card backed by users/{uid} myProperty* fields.
 */
export function buildLegacyVirtualProperty(userProfile) {
  const patch = propertyRecordToMyPropertyPatch(
    clientFormToPropertyRecordShape(clientPropertyFormFromProfile(userProfile))
  );
  const address = resolvePropertyCardAddress({ ...patch, fullAddress: patch.myPropertyFullAddress });
  return {
    id: LEGACY_CLIENT_PROPERTY_ID,
    isLegacyVirtual: true,
    label: "Primary property",
    isPrimary: true,
    status: "owned",
    ...clientFormToPropertyRecordShape(clientPropertyFormFromProfile(userProfile)),
    fullAddress: patch.myPropertyFullAddress || address,
    cardAddress: address,
  };
}

function clientFormToPropertyRecordShape(form) {
  const f = form || {};
  const assessed = f.myPropertyParcelAssessedValue;
  return {
    unit: safeStr(f.myPropertyUnit) || null,
    streetLine: safeStr(f.myPropertyStreetLine) || safeStr(f.myPropertyStreetAddress) || null,
    city: safeStr(f.myPropertyCity) || null,
    province: safeStr(f.myPropertyProvince) || null,
    postalCode: safeStr(f.myPropertyPostalCode) || safeStr(f.myPropertyPostal) || null,
    fullAddress: safeStr(f.myPropertyFullAddress) || safeStr(f.myPropertyAddressLine) || null,
    neighbourhood: safeStr(f.myPropertyNeighbourhood) || null,
    propertyType: safeStr(f.myPropertyType) || null,
    homeownerStatus: safeStr(f.myPropertyHomeownerStatus) || null,
    lat: f.myPropertyLat === "" || f.myPropertyLat == null ? null : Number(f.myPropertyLat),
    lng: f.myPropertyLng === "" || f.myPropertyLng == null ? null : Number(f.myPropertyLng),
    geocode: f.myPropertyGeocode || null,
    parcelSnapshot: f.myPropertyParcelSnapshot || null,
    parcelRoll: safeStr(f.myPropertyParcelRoll) || null,
    parcelYear: safeStr(f.myPropertyParcelYear) || null,
    parcelCommunity: safeStr(f.myPropertyParcelCommunity) || null,
    parcelAssessedValue:
      assessed === "" || assessed == null
        ? null
        : Number.isFinite(Number(assessed))
          ? Number(assessed)
          : null,
    marketSnapshotInputs: null,
    notes: safeStr(f.myPropertyNotes) || null,
    mortgageRenewalDate: safeStr(f.myPropertyMortgageRenewalDate) || null,
    insuranceRenewalDate: safeStr(f.myPropertyInsuranceRenewalDate) || null,
    lawyerName: safeStr(f.myPropertyLawyerName) || null,
    mortgageBrokerName: safeStr(f.myPropertyMortgageBrokerName) || null,
    insuranceContactName: safeStr(f.myPropertyInsuranceContactName) || null,
  };
}

/**
 * Merge selected property into a profile-shaped object for downstream RE components.
 */
export function propertyRecordToProfileContext(userProfile, property) {
  const base = userProfile && typeof userProfile === "object" ? { ...userProfile } : {};
  if (!property || property.isLegacyVirtual) return base;
  return {
    ...base,
    ...propertyRecordToMyPropertyPatch(property),
    reportCoverPhotoUrl: safeStr(property.reportCoverPhotoUrl) || null,
    coverImageUrl: safeStr(property.coverImageUrl) || null,
    photoUrl: safeStr(property.photoUrl) || null,
  };
}

export function resolvePropertyCardAddress(record) {
  const r = record || {};
  const full = safeStr(r.fullAddress);
  if (full) return full;
  const built = formatMyPropertyAddressLine({
    unit: r.unit,
    street: r.streetLine || r.streetAddress,
    city: r.city,
    postal: r.postalCode || r.postal,
  });
  if (built) return built;
  return safeStr(r.label) || "Address not added yet";
}

/**
 * Portfolio list: Firestore records, or a single legacy virtual property when none exist.
 */
export function resolveClientPropertyList({ firestoreProperties, userProfile }) {
  const rows = (firestoreProperties || []).map((p) => ({
    ...p,
    isLegacyVirtual: false,
    status: normalizeClientPropertyStatus(p.status),
    nickname: getPropertyNickname(p),
    community: getPropertyCommunity(p),
    linkedProjectIds: normalizeLinkedProjectIds(p.linkedProjectIds),
    photoUrl: safeStr(p.photoUrl) || null,
    cardAddress: resolvePropertyCardAddress(p),
  }));
  if (rows.length > 0) {
    return sortClientPropertiesForDisplay(rows);
  }
  return [buildLegacyVirtualProperty(userProfile)];
}

export function sortClientPropertiesForDisplay(properties) {
  return [...(properties || [])].sort((a, b) => {
    if (a.isPrimary && !b.isPrimary) return -1;
    if (!a.isPrimary && b.isPrimary) return 1;
    const archivedA = isArchivedClientProperty(a);
    const archivedB = isArchivedClientProperty(b);
    if (archivedA !== archivedB) return archivedA ? 1 : -1;
    return safeStr(a.label).localeCompare(safeStr(b.label));
  });
}

export function pickDefaultSelectedPropertyId(properties, previousId) {
  const list = properties || [];
  if (!list.length) return LEGACY_CLIENT_PROPERTY_ID;
  const prev = safeStr(previousId);
  if (prev && list.some((p) => p.id === prev)) return prev;
  const primary = list.find((p) => p.isPrimary && !isArchivedClientProperty(p));
  if (primary) return primary.id;
  const active = list.find((p) => !isArchivedClientProperty(p));
  return active?.id || list[0].id;
}

export function findSelectedProperty(properties, selectedId) {
  const id = safeStr(selectedId);
  return (properties || []).find((p) => p.id === id) || properties?.[0] || null;
}

/**
 * Firestore payload for users/{uid}/myProperties/{propertyId} create/update.
 */
export function normalizeLinkedProjectIds(value) {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.map((id) => safeStr(id)).filter(Boolean))];
}

export function linkProjectIdToProperty(linkedProjectIds, projectId) {
  const pid = safeStr(projectId);
  if (!pid) return normalizeLinkedProjectIds(linkedProjectIds);
  const next = normalizeLinkedProjectIds(linkedProjectIds);
  if (!next.includes(pid)) next.push(pid);
  return next;
}

export function unlinkProjectIdFromProperty(linkedProjectIds, projectId) {
  const pid = safeStr(projectId);
  if (!pid) return normalizeLinkedProjectIds(linkedProjectIds);
  return normalizeLinkedProjectIds(linkedProjectIds).filter((id) => id !== pid);
}

export function getPropertyNickname(record) {
  return safeStr(record?.nickname) || safeStr(record?.label) || "Property";
}

export function getPropertyCommunity(record) {
  return (
    safeStr(record?.community) ||
    safeStr(record?.parcelCommunity) ||
    safeStr(record?.neighbourhood) ||
    ""
  );
}

export function buildClientPropertyRecordPayload({
  form,
  label,
  nickname,
  community,
  photoUrl,
  reportCoverPhotoUrl,
  coverImageUrl,
  linkedProjectIds,
  status,
  isPrimary,
  marketSnapshotInputs,
  cmaIntelligenceInputs,
}) {
  const normalized = normalizeClientPropertyInput(form);
  const street = safeStr(normalized.myPropertyStreetLine);
  const postal = safeStr(normalized.myPropertyPostalCode);
  const full =
    safeStr(normalized.myPropertyFullAddress) ||
    formatMyPropertyAddressLine({
      unit: normalized.myPropertyUnit,
      street,
      city: normalized.myPropertyCity,
      postal,
    }) ||
    null;

  const resolvedNickname = safeStr(nickname) || safeStr(label) || "Property";

  return {
    label: resolvedNickname,
    nickname: resolvedNickname,
    isPrimary: Boolean(isPrimary),
    status: normalizeClientPropertyStatus(status),
    community: safeStr(community) || normalized.myPropertyNeighbourhood || null,
    photoUrl: safeStr(photoUrl) || null,
    reportCoverPhotoUrl: safeStr(reportCoverPhotoUrl) || null,
    coverImageUrl: safeStr(coverImageUrl) || null,
    linkedProjectIds: normalizeLinkedProjectIds(linkedProjectIds),
    unit: normalized.myPropertyUnit,
    streetLine: street || null,
    city: normalized.myPropertyCity,
    province: normalized.myPropertyProvince,
    postalCode: postal || null,
    fullAddress: full,
    neighbourhood: normalized.myPropertyNeighbourhood,
    propertyType: normalized.myPropertyType,
    homeownerStatus: normalized.myPropertyHomeownerStatus,
    lat: normalized.myPropertyLat,
    lng: normalized.myPropertyLng,
    geocode: normalized.myPropertyGeocode,
    parcelSnapshot: normalized.myPropertyParcelSnapshot,
    parcelRoll: normalized.myPropertyParcelRoll,
    parcelYear: normalized.myPropertyParcelYear,
    parcelCommunity: normalized.myPropertyParcelCommunity,
    parcelAssessedValue: normalized.myPropertyParcelAssessedValue,
    marketSnapshotInputs: marketSnapshotInputs ?? null,
    cmaIntelligenceInputs: cmaIntelligenceInputs ?? null,
    notes: normalized.myPropertyNotes,
    mortgageRenewalDate: normalized.myPropertyMortgageRenewalDate,
    insuranceRenewalDate: normalized.myPropertyInsuranceRenewalDate,
    lawyerName: normalized.myPropertyLawyerName,
    mortgageBrokerName: normalized.myPropertyMortgageBrokerName,
    insuranceContactName: normalized.myPropertyInsuranceContactName,
  };
}

/** Sync primary property record fields onto users/{uid} myProperty* for backward compatibility. */
export function buildLegacyUserPropertySyncPayload(form) {
  return buildClientPropertyUpdatePayload(form);
}

export function usesLegacyPropertyDocuments(propertyId) {
  return isLegacyClientPropertyId(propertyId);
}

export function defaultNewPropertyLabel(existingCount) {
  const n = Number(existingCount) || 0;
  if (n <= 0) return "Primary property";
  return `Property ${n + 1}`;
}
